import React from 'react';
import { Stack, Box, Button, IconButton, Typography, Tooltip, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import GroupRemoveIcon from '@mui/icons-material/GroupRemove';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PendingIcon from '@mui/icons-material/Pending';
import ReplayIcon from '@mui/icons-material/Replay';
import LockIconWrapper from '../../../components/LockIconWrapper';
import EditableText from './EditableText';
import GroupNameDisplay from '../../components/GroupNameDisplay';
import { isTodayOrFuture } from '../../../../util/dateUtils';
import dayjs from 'dayjs';

function BookingHeader({
  booking,
  onClose,
  onPresentClick,
  onRewindCheckinClick,
  onRewindCheckoutClick,
  onShiftClick,
  onCancelClick,
  onSpontaneousReductionClick,
  canSpontaneousReduce = false,
  onGoodwillCancelClick,
  canGoodwillCancel = false,
  onCheckoutClick,
  onUpdateFirstName,
  onUpdateLastName,
  presentCountdown,
  cancelCountdown,
  checkoutCountdown,
  lockCancelBtn,
  lockCheckoutBtn,
  isEditable,
  isWorkflowEditable,
  hasUpdateRights,
  hasMinorUpdateRights,
  hasFullUpdateRights,
  hasUnlimitedEditRights,
  hasRewindCheckinRights,
  isTodayorFutue,
  isToday,
  isWithinNext70Hours,
  hasLastMinuteCancelRights,
  hasPastCancelRights,
  currentlyEditingField,
  onFieldEditStart,
  onFieldEditEnd,
  onConfirmClick,
  hasConfirmRights,
  isRegiondoOnlinePaidBooking = false,
  canCancelRegiondoOnlinePaidBooking = false,
  hasChangeGroupTypeRights = false,
  onGroupTypeBadgeClick,
  changingGroupType = false,
}) {
  const hasWorkflowRights = hasUpdateRights || hasMinorUpdateRights || hasPastCancelRights;
  const canUseWorkflowActions =
    (isTodayorFutue || hasPastCancelRights || hasUnlimitedEditRights) && hasWorkflowRights;

  // canSpontaneousReduce keeps the action row visible for past-day bookings whose
  // spontaneous-reduction window is still open (late-reduction right: up to one month
  // after checkout), so the MOP-show voucher button remains reachable.
  const canUseWorkflowActionRow =
    canUseWorkflowActions || (canSpontaneousReduce && hasWorkflowRights);

  // A booking that starts before midnight and ends after it keeps the previous calendar
  // day as its startTime, so isTodayorFutue turns false at 00:00 while the guests are
  // still on site — and the checkout button disappeared with it. Checking out closes a
  // check-in that already happened and the backend (markAsFinished) has no day condition
  // at all, so a booking whose end has not left the current day stays checkout-ready.
  const runsIntoCurrentDay = isTodayOrFuture(booking?.endTime);
  const canUseCheckoutAction =
    canUseWorkflowActionRow || (runsIntoCurrentDay && hasWorkflowRights);

  const rewindMeta = (() => {
    if (!booking?.presentSince || booking?.finishedAt) return { visible: false };
    const ps = dayjs(booking.presentSince);
    if (!ps.isValid()) return { visible: false };

    const graceMinutes = 10;
    const maxMinutes = graceMinutes + 24 * 60;
    const ageMinutes = dayjs().diff(ps, 'minute', true);

    if (ageMinutes > maxMinutes) return { visible: false };

    const requiresRight = ageMinutes > graceMinutes;
    const hasRight = Boolean(hasRewindCheckinRights);
    const allowed = !requiresRight || hasRight;

    return {
      visible: true,
      disabled: !allowed,
      tooltip: allowed
        ? 'Check-in zurücksetzen'
        : 'Check-in zurücksetzen (Benötigt Sonderrecht)',
    };
  })();

  const rewindCheckoutMeta = (() => {
    if (!booking?.finishedAt) return { visible: false };
    const fa = dayjs(booking.finishedAt);
    if (!fa.isValid()) return { visible: false };

    const graceMinutes = 10;
    const maxMinutes = graceMinutes + 24 * 60;
    const ageMinutes = dayjs().diff(fa, 'minute', true);

    if (ageMinutes > maxMinutes) return { visible: false };

    const requiresRight = ageMinutes > graceMinutes;
    const hasRight = Boolean(hasRewindCheckinRights);
    const allowed = !requiresRight || hasRight;

    return {
      visible: true,
      disabled: !allowed,
      tooltip: allowed
        ? 'Checkout zurücksetzen'
        : 'Checkout zurücksetzen (Benötigt Sonderrecht)',
    };
  })();

  const isCheckinShiftWindowExpired = (() => {
    if (!booking?.presentSince) return false;
    const presentSince = dayjs(booking.presentSince);
    const expiresAt = presentSince.isValid() ? presentSince.add(1, 'hour') : null;
    if (!expiresAt) return true;
    return dayjs().isAfter(expiresAt);
  })();

  return (
    <Box sx={{ position: 'relative', mb: 2 }}>
      <IconButton
        data-testid='booking-details-close'
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: '-20px',
          right: '-20px',
          zIndex: 1,
        }}
      >
        <CloseIcon />
      </IconButton>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent='space-between'
        sx={{ pr: 6, flexWrap: 'wrap', gap: { xs: 1, md: 2 } }}
      >
        <Stack direction='column' spacing={0.5} alignItems='flex-start'>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <EditableText
              value={booking.firstName}
              onSave={onUpdateFirstName}
              disabled={
                !isEditable ||
                !hasFullUpdateRights ||
                (currentlyEditingField && currentlyEditingField !== 'firstName')
              }
              variant='h6'
              sx={{ fontWeight: 'bold', wordBreak: 'break-word' }}
              onEditStart={() => onFieldEditStart('firstName')}
              onEditEnd={onFieldEditEnd}
            />
            <EditableText
              value={booking.lastName}
              onSave={onUpdateLastName}
              disabled={
                !isEditable ||
                !hasFullUpdateRights ||
                (currentlyEditingField && currentlyEditingField !== 'lastName')
              }
              variant='h6'
              sx={{ fontWeight: 'bold', wordBreak: 'break-word' }}
              onEditStart={() => onFieldEditStart('lastName')}
              onEditEnd={onFieldEditEnd}
            />
            {booking?.ltSpecific?.groupType && (
              <Tooltip
                title={
                  booking.ltSpecific.groupType === 'kids'
                    ? 'Kindergruppe (Klicken zum Ändern)'
                    : 'Erwachsenengruppe (Klicken zum Ändern)'
                }
              >
                <Box
                  component='span'
                  onClick={
                    hasChangeGroupTypeRights && isEditable && !changingGroupType
                      ? onGroupTypeBadgeClick
                      : undefined
                  }
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    lineHeight: 1,
                    color: '#fff',
                    backgroundColor:
                      booking.ltSpecific.groupType === 'kids' ? 'orange' : 'green',
                    flexShrink: 0,
                    cursor:
                      hasChangeGroupTypeRights && isEditable && !changingGroupType
                        ? 'pointer'
                        : 'default',
                    opacity: changingGroupType ? 0.5 : 1,
                  }}
                >
                  {changingGroupType ? (
                    <CircularProgress size={12} sx={{ color: '#fff' }} />
                  ) : booking.ltSpecific.groupType === 'kids' ? (
                    'K'
                  ) : (
                    'E'
                  )}
                </Box>
              </Tooltip>
            )}
          </Box>
          {booking.ltSpecific?.groupName && (
            <Typography
              variant='subtitle1'
              sx={{
                fontStyle: 'italic',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: 'action.hover',
              }}
            >
              „<GroupNameDisplay name={booking.ltSpecific.groupName} />“
            </Typography>
          )}
        </Stack>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
          sx={{ flexShrink: 0, minWidth: 0 }}
        >
          {isRegiondoOnlinePaidBooking ? (
            <>
              {booking.presentSince && (
                <>
                  <Box
                    sx={{
                      border: '2px solid',
                      borderColor: 'success.main',
                      borderRadius: '8px',
                      padding: '7px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Typography variant='body1'>
                      <b>Anwesend seit:</b>{' '}
                      {dayjs(booking.presentSince).format('DD.MM.YYYY HH:mm')}
                    </Typography>
                    {rewindMeta.visible && (
                      <Tooltip title={rewindMeta.tooltip}>
                        <Box sx={{ flexShrink: 0 }} data-ignore-card-click>
                          <IconButton
                            size='small'
                            onClick={onRewindCheckinClick}
                            disabled={rewindMeta.disabled}
                            sx={{
                              padding: 0,
                              opacity: rewindMeta.disabled ? 0.35 : 0.6,
                              '&:hover': { opacity: 0.9 },
                            }}
                          >
                            <ReplayIcon fontSize='small' />
                          </IconButton>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                  {!booking.finishedAt ? (
                    canUseCheckoutAction ? (
                      <Button
                        data-testid='booking-details-checkout'
                        variant='outlined'
                        color='error'
                        onClick={onCheckoutClick}
                        disabled={lockCheckoutBtn || !isWorkflowEditable}
                        sx={{
                          border: '2px solid',
                          padding: '7px',
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: 'rgba(211, 47, 47, 0.3)',
                            border: '2px solid',
                            borderColor: 'error.main',
                          },
                        }}
                      >
                        {checkoutCountdown > 0
                          ? `Auschecken? (${checkoutCountdown}s)`
                          : 'Auschecken'}
                      </Button>
                    ) : null
                  ) : (
                    <Box
                      sx={{
                        border: '2px solid',
                        borderColor: 'error.main',
                        borderRadius: '8px',
                        padding: '7px',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Typography variant='body1'>
                        <b>Ausgecheckt am:</b>{' '}
                        {dayjs(booking.finishedAt).format('DD.MM.YYYY HH:mm')}
                      </Typography>
                      {rewindCheckoutMeta.visible && (
                        <Tooltip title={rewindCheckoutMeta.tooltip}>
                          <Box sx={{ flexShrink: 0 }} data-ignore-card-click>
                            <IconButton
                              size='small'
                              onClick={onRewindCheckoutClick}
                              disabled={rewindCheckoutMeta.disabled}
                              sx={{
                                padding: 0,
                                opacity: rewindCheckoutMeta.disabled ? 0.35 : 0.6,
                                '&:hover': { opacity: 0.9 },
                              }}
                            >
                              <ReplayIcon fontSize='small' />
                            </IconButton>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </>
              )}

              {booking.status !== 'blocked' && booking.status !== 'canceled' && (
                <>
                  {!booking.presentSince && isToday && canUseWorkflowActions && (
                    <Button
                      data-testid='booking-details-present'
                      variant='contained'
                      color='success'
                      onClick={onPresentClick}
                      startIcon={<CheckCircleIcon />}
                      disabled={!isWorkflowEditable}
                      sx={{
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(76, 175, 80, 0.3)',
                          color: 'rgba(0, 0, 0, 0.5)',
                        },
                        minWidth: 'fit-content',
                        width: { xs: '100%', sm: '50%', md: 'auto' },
                      }}
                    >
                      {presentCountdown > 0
                        ? `Als anwesend markieren? (${presentCountdown}s)`
                        : 'Als anwesend markieren'}
                    </Button>
                  )}

                  {canCancelRegiondoOnlinePaidBooking ? (
                    <Button
                      data-testid='booking-details-cancel'
                      variant='contained'
                      color='error'
                      onClick={onCancelClick}
                      startIcon={<CancelIcon />}
                      disabled={lockCancelBtn || booking.status === 'noShow'}
                      sx={{
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(211, 47, 47, 0.3)',
                          color: 'rgba(115, 115, 115, 0.5)',
                        },
                        minWidth: 'fit-content',
                        width: { xs: '100%', sm: '50%', md: 'auto' },
                      }}
                    >
                      <LockIconWrapper right='internalBooking:special:regiondoOnlinePaidEdit'>
                        {booking.status === 'awaitingConfirmation'
                          ? cancelCountdown > 0
                            ? `Wirklich ablehnen? (${cancelCountdown}s)`
                            : 'Ablehnen'
                          : cancelCountdown > 0
                            ? `Wirklich stornieren? (${cancelCountdown}s)`
                            : 'Stornieren'}
                      </LockIconWrapper>
                    </Button>
                  ) : null}
                </>
              )}
            </>
          ) : (
            // The row opens for the checkout gate; every entry below the check-in/checkout
            // pair keeps the narrower canUseWorkflowActionRow, so a booking that only runs
            // past midnight gains the checkout and nothing else.
            canUseCheckoutAction && (
              <>
                {booking.presentSince && (
                  <>
                    <Box
                      sx={{
                        border: '2px solid',
                        borderColor: 'success.main',
                        borderRadius: '8px',
                        padding: '7px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Typography variant='body1'>
                        <b>Anwesend seit:</b>{' '}
                        {dayjs(booking.presentSince).format('DD.MM.YYYY HH:mm')}
                      </Typography>
                      {rewindMeta.visible && (
                        <Tooltip title={rewindMeta.tooltip}>
                          <Box sx={{ flexShrink: 0 }} data-ignore-card-click>
                            <IconButton
                              size='small'
                              onClick={onRewindCheckinClick}
                              disabled={rewindMeta.disabled}
                              sx={{
                                padding: 0,
                                opacity: rewindMeta.disabled ? 0.35 : 0.6,
                                '&:hover': { opacity: 0.9 },
                              }}
                            >
                              <ReplayIcon fontSize='small' />
                            </IconButton>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                    {!booking.finishedAt ? (
                      <Button
                        data-testid='booking-details-checkout'
                        variant='outlined'
                        color='error'
                        onClick={onCheckoutClick}
                        disabled={lockCheckoutBtn}
                        sx={{
                          border: '2px solid',
                          padding: '7px',
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: 'rgba(211, 47, 47, 0.3)',
                            border: '2px solid',
                            borderColor: 'error.main',
                          },
                        }}
                      >
                        {checkoutCountdown > 0
                          ? `Auschecken? (${checkoutCountdown}s)`
                          : 'Auschecken'}
                      </Button>
                    ) : (
                      <Box
                        sx={{
                          border: '2px solid',
                          borderColor: 'error.main',
                          borderRadius: '8px',
                          padding: '7px',
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Typography variant='body1'>
                          <b>Ausgecheckt am:</b>{' '}
                          {dayjs(booking.finishedAt).format('DD.MM.YYYY HH:mm')}
                        </Typography>
                        {rewindCheckoutMeta.visible && (
                          <Tooltip title={rewindCheckoutMeta.tooltip}>
                            <Box sx={{ flexShrink: 0 }} data-ignore-card-click>
                              <IconButton
                                size='small'
                                onClick={onRewindCheckoutClick}
                                disabled={rewindCheckoutMeta.disabled}
                                sx={{
                                  padding: 0,
                                  opacity: rewindCheckoutMeta.disabled ? 0.35 : 0.6,
                                  '&:hover': { opacity: 0.9 },
                                }}
                              >
                                <ReplayIcon fontSize='small' />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </>
                )}

                {canUseWorkflowActionRow &&
                  booking.status === 'awaitingConfirmation' &&
                  hasConfirmRights && (
                    <Button
                      data-testid='booking-details-confirm'
                      variant='contained'
                      color='success'
                      onClick={onConfirmClick}
                      startIcon={<CheckCircleIcon />}
                      sx={{
                        minWidth: 'fit-content',
                        width: { xs: '100%', sm: '50%', md: 'auto' },
                      }}
                    >
                      <LockIconWrapper right='internalBooking:special:confirmpending'>
                        Bestätigen
                      </LockIconWrapper>
                    </Button>
                  )}
                {canUseWorkflowActionRow && booking.status !== 'blocked' && (
                  <>
                    {!booking.presentSince && isToday && (
                      <Button
                        data-testid='booking-details-present'
                        variant='contained'
                        color='success'
                        onClick={onPresentClick}
                        startIcon={<CheckCircleIcon />}
                        disabled={!isWorkflowEditable}
                        sx={{
                          '&.Mui-disabled': {
                            backgroundColor: 'rgba(76, 175, 80, 0.3)',
                            color: 'rgba(0, 0, 0, 0.5)',
                          },
                          minWidth: 'fit-content',
                          width: { xs: '100%', sm: '50%', md: 'auto' },
                        }}
                      >
                        {presentCountdown > 0
                          ? `Als anwesend markieren? (${presentCountdown}s)`
                          : 'Als anwesend markieren'}
                      </Button>
                    )}
                    <Button
                      data-testid='booking-details-shift'
                      variant='contained'
                      color='primary'
                      onClick={onShiftClick}
                      startIcon={<CalendarTodayIcon />}
                      disabled={
                        (!isEditable && !hasUnlimitedEditRights) ||
                        !hasUpdateRights ||
                        isCheckinShiftWindowExpired ||
                        (['KaraokeZone', 'ExitZone'].includes(booking.entertainment) &&
                          !booking.resourceId)
                      }
                      sx={{
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(25, 118, 210, 0.3)',
                          color: 'rgba(0, 0, 0, 0.5)',
                        },
                        color: theme =>
                          theme.palette.mode === 'dark' ? theme.palette.common.black : undefined,
                        minWidth: 'fit-content',
                        width: { xs: '100%', sm: '50%', md: 'auto' },
                      }}
                    >
                      <LockIconWrapper right={'internalBooking:update:own'}>
                        Verschieben
                      </LockIconWrapper>
                    </Button>
                  </>
                )}
                {canUseWorkflowActionRow &&
                  (hasUnlimitedEditRights ||
                    hasLastMinuteCancelRights ||
                    !isWithinNext70Hours ||
                    booking.tags.includes('hasInsurance') ||
                    (booking.status === 'awaitingConfirmation' && hasConfirmRights)) && (
                  <Button
                    data-testid='booking-details-cancel'
                    variant='contained'
                    color='error'
                    onClick={onCancelClick}
                    startIcon={<CancelIcon />}
                    disabled={
                      lockCancelBtn ||
                      booking.status === 'noShow' ||
                      (booking.status === 'awaitingConfirmation'
                        ? !hasConfirmRights
                        : (!isEditable && !hasUnlimitedEditRights && !hasPastCancelRights) ||
                          (!hasUpdateRights && !hasPastCancelRights))
                    }
                    sx={{
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(211, 47, 47, 0.3)',
                        color: 'rgba(115, 115, 115, 0.5)',
                      },
                      minWidth: 'fit-content',
                      width: { xs: '100%', sm: '50%', md: 'auto' },
                    }}
                  >
                    <LockIconWrapper
                      right={
                        booking.status === 'awaitingConfirmation'
                          ? 'internalBooking:special:confirmpending'
                          : hasPastCancelRights
                            ? 'internalBooking:special:cancelPast'
                            : 'internalBooking:special:cancelLastMinute'
                      }
                    >
                      {booking.status === 'awaitingConfirmation'
                        ? cancelCountdown > 0
                          ? `Wirklich ablehnen? (${cancelCountdown}s)`
                          : 'Ablehnen'
                        : cancelCountdown > 0
                          ? `Wirklich stornieren? (${cancelCountdown}s)`
                          : 'Stornieren'}
                    </LockIconWrapper>
                  </Button>
                )}
                {canUseWorkflowActionRow && canSpontaneousReduce && (
                  <Button
                    data-testid='booking-details-spontaneous-reduction'
                    variant='contained'
                    color='warning'
                    onClick={onSpontaneousReductionClick}
                    startIcon={<GroupRemoveIcon />}
                    sx={{
                      minWidth: 'fit-content',
                      width: { xs: '100%', sm: '50%', md: 'auto' },
                    }}
                  >
                    <LockIconWrapper right='internalBooking:special:spontaneousReduction'>
                      Spontan weniger Personen
                    </LockIconWrapper>
                  </Button>
                )}
                {canUseWorkflowActionRow && canGoodwillCancel && (
                  <Button
                    data-testid='booking-details-goodwill-cancel'
                    variant='outlined'
                    color='error'
                    onClick={onGoodwillCancelClick}
                    startIcon={<LocalActivityIcon />}
                    sx={{
                      minWidth: 'fit-content',
                      width: { xs: '100%', sm: '50%', md: 'auto' },
                    }}
                  >
                    Storno mit Kulanz-Gutschein
                  </Button>
                )}
              </>
            )
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

export default BookingHeader;
